import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Briefcase, Brain, Settings, Users, CheckCircle2, Linkedin } from 'lucide-react';

const About = () => {
  const philosophy = [
    {
      icon: Briefcase,
      title: 'Business-First',
      description: 'People strategy is a driver of growth and execution.'
    },
    {
      icon: Target,
      title: 'Context-Led',
      description: 'Design reflects stage, market, ownership and ambition.'
    },
    {
      icon: Brain,
      title: 'Human Judgment, Enhanced by Data and AI',
      description: 'Technology strengthens decisions; it doesn\'t replace them.'
    },
    {
      icon: Settings,
      title: 'Clarity by Design',
      description: 'Roles, structure, governance, culture, and decision rights are explicit.'
    },
    {
      icon: Users,
      title: 'Ownership Embedded',
      description: 'Transformation is co-created with promoters, CXOs, and HR leaders.'
    },
    {
      icon: CheckCircle2,
      title: 'Practical by Intent',
      description: 'No buzzwords. No generic models. Only change that endures.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2069&q=80"
            alt="Executive boardroom"
            className="w-full h-full object-cover opacity-15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
              About NexStep
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                Building execution-ready organisations
              </span>
            </h1>
            <p className="text-xl lg:text-2xl max-w-3xl font-light mb-12 text-slate-300">
              At critical moments where people, leadership, and systems determine outcomes and value.
            </p>
          </div>
        </div>
      </section>

      {/* Sustainable Growth Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Sustainable growth is built through people
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Organisations struggle not because of a lack of frameworks, but because transformation is often disconnected from context, culture, and ownership.
              </p>
              <div className="p-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                <p className="text-slate-700 leading-relaxed">
                  At NexStep, our role is to bring clarity where complexity accumulates. We help organisations anchor people transformation in business priorities, shape it to their operating reality, and ensure it is owned and executed - not merely designed.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/l1apgqvl_Image%2014%20-%20About%20Us%20-%20Sustainable%20growth%20is%20built%20through%20people.jpg"
                alt="Sustainable growth through people"
                className="rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Our Philosophy
            </h2>
            <p className="text-lg text-amber-600 font-medium mb-4">
              What enabled success so far rarely supports the next phase
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our work is guided by a few principles that shape how we think and execute.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {philosophy.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-amber-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{principle.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operating Across Borders */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_1549dae8-750d-40d4-95b1-202e48d84d83/artifacts/8uchcx4u_Image%2015%20-%20About%20Us%20-%20Operating%20Across%20Borders.jpg"
                alt="Operating across borders"
                className="rounded-xl shadow-xl" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Operating Across Borders
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Rooted in India's growth ecosystem, we work across international markets - supporting Indian enterprises as they expand globally and global organisations seeking local insight.
                </p>
                <p>
                  We understand the realities of high-growth environments, where talent constraints and execution discipline matter more than perfect models.
                </p>
                <p>
                  Our approach travels well because it is principle-led, context-aware, and built to adapt across markets.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 lg:py-28 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 leading-tight">
              Principal Consultants
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Ujjwal Raut */}
            <div className="p-10 bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="flex flex-col">
                <div className="flex gap-6 mb-6">
                  <div className="w-48 flex-shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_hr-transformation/artifacts/s5cknt7f_Ujjwal%20Raut%20Resized.PNG"
                      alt="Ujjwal Raut"
                      className="rounded-lg w-full" />
                  </div>
                  <div className="flex-1 flex flex-col justify-end">
                    <h3 className="text-2xl text-slate-900 mb-2">Ujjwal Raut</h3>
                    <p className="text-amber-600 text-sm tracking-wide">Founder & Principal Consultant</p>
                  </div>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">
                    25+ years of experience supporting organisations at strategic inflection points - succession, rapid growth and restructuring.
                  </p>
                  <p className="leading-relaxed">
                    Worked with 50+ organisations across India, the Middle East, Africa, and Europe.
                  </p>
                  <p className="leading-relaxed">
                    Former HR Leader at: eClerx, ABC Consultants and Datamatics
                  </p>
                  <p className="leading-relaxed">
                    Advisor to multiple global and regional organisations.
                  </p>
                  <a
                    href="https://www.linkedin.com/in/ujjwalraut/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 mt-4 text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    <Linkedin size={20} />
                    <span className="text-sm">Connect on LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Rushabh Mota */}
            <div className="p-10 bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="flex flex-col">
                <div className="flex gap-6 mb-6">
                  <div className="w-48 flex-shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_hr-transformation/artifacts/lopurylr_Rushabh%20Mota%20-%20Resized.JPG"
                      alt="Rushabh Mota"
                      className="rounded-lg w-full" />
                  </div>
                  <div className="flex-1 flex flex-col justify-end">
                    <h3 className="text-2xl text-slate-900 mb-2">Rushabh Mota</h3>
                    <p className="text-amber-600 text-sm tracking-wide">Principal Consulting Partner</p>
                  </div>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">
                    12+ years of experience across FMCG, Pharma, Logistics, Manufacturing, Wellness, and Retail.
                  </p>
                  <p className="leading-relaxed">
                    Specialist in AI-enabled HR systems, organisation design, and manager capability building.
                  </p>
                  <p className="leading-relaxed">
                    Former HR leader at: Cipla, GAP (GCC), and Schindler
                  </p>
                  <p className="leading-relaxed">
                    Advisor to leading global organisations.
                  </p>
                  <a
                    href="https://www.linkedin.com/in/rushabhmota/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 mt-4 text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    <Linkedin size={20} />
                    <span className="text-sm">Connect on LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              When organisational readiness matters most,
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              an informed conversation is a good place to begin.
            </p>
            <Link
              to="/contact#ready-to-move"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-slate-900 font-semibold rounded-md hover:bg-amber-400 transition-all duration-200 shadow-lg group">
              Let's Connect
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
